<template>
    <div :class="flex && 'd-flex'">
        <input-text
            v-if="filterable"
            ref="searchBox"
            v-model:value="searchText"
            class="m-12"
            :disabled="disabled"
            :placeholder="ucfirst($t('common.filter'))"
            icon="search"
        >
            <template #left>
                <v-icon
                    class="text-gray-dark"
                    name="search"
                    :w="20"
                />
            </template>
        </input-text>

        <ul
            v-if="filteredOptions.length > 0"
            ref="list"
            class="overflow-auto py-0 bg-light selectable--list"
            :class="listClass"
        >
            <li
                v-for="(item, index) in filteredOptions"
                :key="index"
                class="px-16 cursor--pointer text-dark"
                :class="isSelected(item.value) && checkedClass"
                tabindex="0"
                @click.prevent="toggleSelect(item)"
                @keydown.prevent.stop
                @keydown.down="moveFocus($event, 'down')"
                @keydown.up="moveFocus($event, 'up')"
                @keydown.enter="toggleSelect(item)"
            >
                <input-checkbox
                    v-if="multiple"
                    :value="isSelected(item.value)"
                >
                    <truncated-text
                        :text="item.text"
                        :lines="1"
                    />
                </input-checkbox>

                <truncated-text
                    v-else
                    :text="item.text"
                    :lines="1"
                />
            </li>
        </ul>

        <div
            v-if="options.length === 0"
            class="bg-gray-light text-dark text-center py-16"
        >
            <v-icon
                name="info"
                :w="42"
            />

            <p>
                {{ ucfirst($t("validation.noResults")) }}
            </p>
        </div>
    </div>
</template>

<script>
import { ucfirst } from "~/lib/utils"
import InputText from "~/components/inputs/InputText"
import InputCheckbox from "~/components/inputs/InputCheckbox"
import without from "lodash/without"
import TruncatedText from "~/components/elements/TruncatedText"

export default {
    props: {
        options: {
            type: Array,
            required: true,
        },

        listClass: String,
        flex: Boolean,
        disabled: Boolean,
        filterable: Boolean,
        multiple: Boolean,
        value: {
            type: [String, Number, Array],
            default: () => [],
        },

        isFocused: Boolean,
        nullOption: String,
    },

    emits: ["blur", "update:value"],

    data() {
        return {
            focusedIndex: 0,
            searchText: "",
        }
    },

    computed: {
        filteredOptions() {
            const options = this.options.filter(item => {
                const search = new RegExp(this.searchText.toLowerCase())
                const value = item.text.toLowerCase()
                if (search.test(value)) {
                    return item
                }
            })

            if (!this.multiple && this.nullOption) {
                options.unshift({
                    text: this.nullOption,
                    value: null,
                })
            }

            return options
        },

        checkedClass() {
            return [this.$css(this.$theme.textPrimary), "font-weight-semi-bold"]
        },
    },

    watch: {
        isFocused() {
            this.isFocused ? this.focusIn() : this.focusOut()
        },
    },

    created() {
        if (this.multiple && typeof this.value == "string") {
            throw new Error(
                `When the 'multiple' prop is passed, the 'value' prop must be an array.`
            )
        }
    },

    methods: {
        calcMultipleSelection(value) {
            if (this.isSelected(value)) {
                return without(this.value, value)
            } else {
                return [...this.value, value]
            }
        },

        toggleSelect(item) {
            if (this.disabled) return
            let newValue
            if (this.multiple) {
                newValue = this.calcMultipleSelection(item.value)
            } else {
                newValue = item.value
            }
            this.$emit("update:value", newValue)
        },

        isSelected(value) {
            const isSelected = this.multiple
                ? this.value.includes(value)
                : this.value === value
            return isSelected
        },

        focusIn() {
            if (this.filterable) {
                this.$refs.searchBox.setFocus()
            } else if (this.filteredOptions.length) {
                this.$refs.list
                    .querySelectorAll("li")
                    [this.focusedIndex].focus()
            }
        },

        focusOut() {
            if (!this.filteredOptions.length) {
                this.$emit("blur")
                return
            }
            let items = this.$refs.list.querySelectorAll("li")
            items.forEach(item => {
                item.setAttribute("tabindex", -1)
            })
            this.$emit("blur")
        },

        moveFocus(evt, direction) {
            let curr = evt.target
            let next =
                direction === "down" ? curr.nextSibling : curr.previousSibling
            if (next) {
                curr.setAttribute("tabindex", -1)
                next.setAttribute("tabindex", 0)
                next.focus()
            }
        },

        ucfirst,
    },

    components: {
        InputText,
        InputCheckbox,
        TruncatedText,
    },
}
</script>
